import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { type Database } from "@fitness-app/supabase";

import { useUserClaims } from "~/hooks/trainer/useUserClaims";
import { supabase } from "~/store/initializeStore";

import { emptyTraineeTargetSpec, type TraineeSegment, type TraineeTargetSpec } from "./types";

type SegmentRow = Database["public"]["Tables"]["trainee_segment"]["Row"];

const SEGMENTS_KEY = ["trainee-segments"];

const parseSpec = (raw: unknown): TraineeTargetSpec => {
  if (raw && typeof raw === "object") {
    return { ...emptyTraineeTargetSpec, ...(raw as Partial<TraineeTargetSpec>) };
  }
  return emptyTraineeTargetSpec;
};

/** Saved, reusable audience specs (`trainee_segment`). RLS scopes them to the caller's org. */
export const useTraineeSegments = () => {
  const queryClient = useQueryClient();
  const { memberId } = useUserClaims();

  const listQuery = useQuery({
    queryKey: SEGMENTS_KEY,
    staleTime: 60_000,
    queryFn: async (): Promise<TraineeSegment[]> => {
      const { data, error } = await supabase
        .from("trainee_segment")
        .select("id, name, spec")
        .order("name", { ascending: true })
        .returns<Pick<SegmentRow, "id" | "name" | "spec">[]>();

      if (error) {
        throw error;
      }

      return (data ?? []).map((row) => ({ id: row.id, name: row.name, spec: parseSpec(row.spec) }));
    },
  });

  const saveMutation = useMutation({
    mutationFn: async ({ name, spec }: { name: string; spec: TraineeTargetSpec }) => {
      const { error } = await supabase.from("trainee_segment").insert({
        name,
        spec: spec as never,
        createdBy: memberId ?? null,
      } as never);

      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SEGMENTS_KEY });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      // Segments are readable across the tenant but only their author / their head coach / the
      // account owner may drop one. RLS answers a blocked DELETE with 204 and zero rows, not with
      // an error — without asking for the deleted row back the button would look like a no-op.
      const { data, error } = await supabase
        .from("trainee_segment")
        .delete()
        .eq("id", id)
        .select("id")
        .returns<{ id: string }[]>();

      if (error) {
        throw new Error(error.message);
      }

      if (!data?.length) {
        throw new Error("Nie masz uprawnień, aby usunąć ten segment");
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SEGMENTS_KEY });
    },
  });

  return {
    segments: listQuery.data ?? [],
    isLoading: listQuery.isLoading,
    saveSegment: saveMutation.mutateAsync,
    deleteSegment: deleteMutation.mutateAsync,
    isSaving: saveMutation.isLoading,
  };
};
